import React, {useMemo, useRef, useState} from "react";
import {motion} from "framer-motion";
import cluesData from "./cluesData";
import CrosswordCell from "./CrosswordCell";
import styles from "../../Parigi.module.css";

const {matrix, words, centerRow} = cluesData;

const cellKey = (r, c) => `${r}-${c}`;

const CrosswordBoard = ({onOpenPostcard}) => {
    const [values, setValues] = useState({});
    const inputRefs = useRef({});

    const solved = useMemo(() => {
        for (let r = 0; r < matrix.length; r++) {
            for (let c = 0; c < matrix[r].length; c++) {
                const expected = matrix[r][c];
                if (expected === null) continue;
                if ((values[cellKey(r, c)] || '') !== expected) return false;
            }
        }
        return true;
    }, [values]);

    const focusCell = (r, c) => {
        const el = inputRefs.current[cellKey(r, c)];
        if (el) el.focus();
    };

    const handleChange = (r, c, raw) => {
        const char = raw.toUpperCase().slice(-1).replace(/[^A-ZÀ-Ü]/g, '');
        setValues(prev => ({...prev, [cellKey(r, c)]: char}));
        if (char && matrix[r + 1]?.[c]) {
            focusCell(r + 1, c);
        }
    };

    const handleKeyDown = (r, c, e) => {
        if (e.key === 'Backspace' && !values[cellKey(r, c)] && matrix[r - 1]?.[c]) {
            e.preventDefault();
            focusCell(r - 1, c);
        }
    };

    const renderCell = (r, c) => {
        const expected = matrix[r][c];
        const numberedWord = words.find(w => w.startRow === r && w.col === c);
        return (
            <div key={cellKey(r, c)} className={styles.cellHolder} style={{gridRow: r + 1, gridColumn: c + 1}}>
                {numberedWord && <span className={styles.clueNumber}>{numberedWord.clueNumber}</span>}
                <CrosswordCell
                    ref={el => (inputRefs.current[cellKey(r, c)] = el)}
                    row={r}
                    col={c}
                    value={values[cellKey(r, c)] || ''}
                    isWrong={!!values[cellKey(r, c)] && values[cellKey(r, c)] !== expected}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                />
            </div>
        );
    };

    return (
        <motion.div
            key="crossword"
            initial={{opacity: 0, y: 12}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, scale: 0.96}}
            transition={{duration: 0.5}}
            className={styles.crosswordWrapper}
        >
            <p className={`text-center mb-3 ${styles.hint}`}>
                Risolvi le parole verticali: l'incrocio centrale nasconde una parola magica ✨
            </p>

            <div className={styles.grid}>
                {matrix.map((rowArr, r) =>
                    r === centerRow ? null : rowArr.map((expected, c) => expected !== null && renderCell(r, c))
                )}

                {solved ? (
                    <button
                        type="button"
                        aria-label="Apri la cartolina"
                        onClick={onOpenPostcard}
                        className={styles.parigiButton}
                        style={{gridRow: centerRow + 1, gridColumn: '1 / span 6'}}
                    >
                        {matrix[centerRow].map((letter, c) => (
                            <span key={c} className={styles.parigiLetter}>{letter}</span>
                        ))}
                    </button>
                ) : (
                    matrix[centerRow].map((expected, c) => renderCell(centerRow, c))
                )}
            </div>

            <div className={styles.cluesList}>
                {words.map(w => (
                    <div key={w.clueNumber} className={styles.clueItem}>
                        <strong>{w.clueNumber}.</strong> {w.clue}
                    </div>
                ))}
            </div>
        </motion.div>
    );
};

export default CrosswordBoard;
